/**
 * Helper utility functions for the application
 */

import dayjs from 'dayjs';

/**
 * Format a date to a readable string
 * @param date Date to format
 * @returns Formatted date string
 */
export const formatDate = (date: Date): string => {
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

/**
 * Format a time to a readable string
 * @param date Date to format
 * @returns Formatted time string
 */
export const formatTime = (date: Date): string => {
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

/**
 * Mask sensitive information like auth tokens
 * Shows only the last 4 characters and masks the rest
 * @param value String to mask
 * @returns Masked string
 */
export const maskSensitiveInfo = (value: string): string => {
  if (!value || value.length <= 4) return value;
  return '•'.repeat(value.length - 4) + value.slice(-4);
};

/**
 * Save data to local storage
 * @param key Storage key
 * @param data Data to store
 */
export const saveToLocalStorage = <T>(key: string, data: T): void => {
  try {
    const serializedData = JSON.stringify(data);
    localStorage.setItem(key, serializedData);
  } catch (error) {
    console.error('Error saving to localStorage:', error);
  }
};

/**
 * Load data from local storage
 * @param key Storage key
 * @returns Stored data or null if not found
 */
export const loadFromLocalStorage = <T>(key: string): T | null => {
  try {
    const serializedData = localStorage.getItem(key);
    if (serializedData === null) return null;
    return JSON.parse(serializedData) as T;
  } catch (error) {
    console.error('Error loading from localStorage:', error);
    return null;
  }
};

/**
 * Clear data from local storage
 * @param key Storage key
 */
export const clearFromLocalStorage = (key: string): void => {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.error('Error clearing localStorage:', error);
  }
};

/**
 * Debounce function to limit the rate at which a function can fire
 * @param func Function to debounce
 * @param wait Wait time in milliseconds
 * @returns Debounced function
 */
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout | null = null;
  
  return (...args: Parameters<T>): void => {
    const later = () => {
      timeout = null;
      func(...args);
    };
    
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Generate a random ID
 * @returns Random ID string
 */
export const generateId = (): string => {
  return Math.random().toString(36).substring(2, 15);
};

/**
 * Check if a string is a valid URL
 * @param str String to check
 * @returns Boolean indicating if string is a valid URL
 */
export const isValidUrl = (str: string): boolean => {
  try {
    new URL(str);
    return true;
  } catch {
    return false;
  }
};

// Format time for display using dayjs (HH:MM:SS → hh:mm A)
export const formatTimeAMPM = (timeString: string): string => {
  try {
    // Check if time is in HH:MM:SS format
    if (timeString.match(/^\d{2}:\d{2}(:\d{2})?$/)) {
      return dayjs(`2000-01-01 ${timeString}`).format('h:mm A');
    }
    return dayjs(timeString).format('h:mm A');
  } catch (e) {
    return timeString || 'N/A';
  }
};

export default {
  formatDate,
  formatTime,
  maskSensitiveInfo,
  saveToLocalStorage,
  loadFromLocalStorage,
  clearFromLocalStorage,
  debounce,
  generateId,
  isValidUrl,
  formatTimeAMPM,
};

